export const Combobox = {
  mounted() {
    if (!this.initElements()) return;

    this.boundHandleScroll = this.updateDropdownPosition.bind(this);
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);
    this.boundOpenButtonClick = this.handleOpenButtonClick.bind(this);
    this.boundSelectFocus = this.handleSelectFocus.bind(this);
    this.boundClearButtonClick = this.handleClearButtonClick.bind(this);

    this.lastNavigatedValue = null; // last chosen option

    // forward focus to the search input
    this.select.addEventListener("focus", this.boundSelectFocus);
    this.openButton.addEventListener("click", this.boundOpenButtonClick);

    this.el.querySelectorAll(".combobox-option").forEach((optionBtnEl) => {
      const handler = this.handleOptionClick.bind(this, optionBtnEl);
      optionBtnEl.addEventListener("click", handler);
    });

    if (this.searchInput) {
      this.boundSearchInputHandler = this.handleSearch.bind(this);
      this.searchInput.addEventListener("input", this.boundSearchInputHandler);
    }

    if (this.clearButton) {
      this.clearButton.addEventListener("click", this.boundClearButtonClick);
    }

    document.addEventListener("click", this.boundHandleDocumentClick, true);

    this.observer = new MutationObserver((mutations) => {
      // toggleOption() below writes the "selected" attribute back onto the
      // option to keep it in sync, which would otherwise re-trigger this same
      // observer. Disconnect for the duration of the callback to avoid that
      // (MutationObserver has no per-node unobserve, only disconnect/observe).
      this.observer.disconnect();
      mutations.forEach((m) => {
        if (this.select.contains(m.target)) {
          this.toggleOption(m.target, null, m.target.selected);
        }
      });
      this.observeSelect();
    });
    this.syncDisplayFromSelect();
    this.observeSelect();
  },

  observeSelect() {
    this.observer.observe(this.select, {
      attributes: true,
      attributeFilter: ["selected"],
      childList: true,
      // Without `subtree`, this only watches the <select> itself: attribute
      // mutations on its <option> children (e.g. the server clearing the
      // selection after "add-app-device-pair") never reach the callback, and
      // the visible label/pill silently goes stale.
      subtree: true,
    });
  },

  initElements() {
    this.select = this.el.querySelector(".combo-select");
    this.dropdown = this.el.querySelector('[data-part="listbox"]');
    this.openButton = this.el.querySelector(".combobox-trigger");

    if (!this.select || !this.dropdown || !this.openButton) {
      return false;
    }

    if (!this.openButton.id) {
      this.openButton.id = `combobox-trigger-${Math.random().toString(36).substring(2, 9)}`;
    }

    this.selectedDisplay = this.el.querySelector(".selected-value");
    this.searchInput = this.el.querySelector(".combobox-search-input");
    this.clearButton = this.el.querySelector('[data-part="clear-combobox-button"]');
    this.placeholder = this.el.querySelector(".combobox-placeholder");

    const excludeIds =
      this.openButton && this.openButton.id != "" ? `, #${this.openButton.id}` : "";
    this.focusableSelector = [
      `a:not([disabled]${excludeIds})`,
      `button:not([disabled]${excludeIds})`,
      `select:not([disabled], .combo-select${excludeIds})`,
      `textarea:not([disabled]${excludeIds})`,
      `input:not([disabled], [type="hidden"]${excludeIds})`,
      `[tabindex]:not([disabled], [tabindex="-1"]${excludeIds})`,
    ].join(", ");
    let pillClasses = this.selectedDisplay.getAttribute("data-pill-class");
    this.pillClasses = (pillClasses || "").split(" ");

    return true;
  },

  isHidden() {
    return this.dropdown.hasAttribute("hidden");
  },

  handleSelectFocus(e) {
    const isEl = e.target.id == this.select.id;

    if (isEl && this.isHidden()) {
      e.preventDefault();
      this.openDropdown();
    }
  },

  handleOpenButtonClick(e) {
    e.preventDefault();

    const isEl = this.el.contains(e.target);
    if (isEl && this.isHidden()) {
      this.openDropdown();
    } else {
      this.closeDropdown();
    }
  },

  openDropdown() {
    this.dropdown.removeAttribute("hidden");
    this.openButton.setAttribute("aria-expanded", "true");

    requestAnimationFrame(() => {
      this.updateDropdownPosition();
      this.dropdownOptions = this.el.querySelectorAll(".combobox-option");

      let navigateTarget = null;
      if (this.lastNavigatedValue) {
        navigateTarget = this.el.querySelector(
          `.combobox-option[data-combobox-value="${this.lastNavigatedValue}"]`,
        );
      }
      if (!navigateTarget) {
        navigateTarget = this.el.querySelector(".combobox-option[data-combobox-selected]");
      }
      if (!navigateTarget && this.dropdownOptions.length > 0) {
        navigateTarget = this.dropdownOptions[0];
      }

      this.dropdownOptions.forEach((opt) => opt.removeAttribute("data-combobox-navigate"));

      if (navigateTarget) {
        if (navigateTarget.getAttribute("data-combobox-disabled") != "true") {
          navigateTarget.setAttribute("data-combobox-navigate", "");
        }
        navigateTarget.scrollIntoView({ block: "nearest" });
        this.lastNavigatedValue = navigateTarget.dataset.comboboxValue;
        if (navigateTarget.id) {
          this.openButton.setAttribute("aria-activedescendant", navigateTarget.id);
        } else {
          this.openButton.removeAttribute("aria-activedescendant");
        }
      } else {
        this.openButton.removeAttribute("aria-activedescendant");
      }

      if (this.searchInput) {
        this.searchInput.focus();
        this.searchInput.scrollIntoView({ block: "nearest", inline: "nearest" });
      }
    });

    // Capture-phase so scrolls inside any ancestor (the modal's scroll
    // container, not just the window) reposition the fixed dropdown.
    document.addEventListener("scroll", this.boundHandleScroll, { passive: true, capture: true });
    window.addEventListener("resize", this.boundHandleScroll, { passive: true });
    document.addEventListener("keydown", this.boundHandleKeyDown);
  },

  closeDropdown() {
    if (!this.isHidden()) {
      this.dropdown.setAttribute("hidden", true);
    }
    this.openButton.setAttribute("aria-expanded", "false");
    this.openButton.removeAttribute("aria-activedescendant");
    this.resetDropdownPosition();
    document.removeEventListener("scroll", this.boundHandleScroll, { capture: true });
    window.removeEventListener("resize", this.boundHandleScroll);
    document.removeEventListener("keydown", this.boundHandleKeyDown);
  },

  resetNavigateToFirstOption() {
    this.el.querySelectorAll(".combobox-option[data-combobox-navigate]").forEach((opt) => {
      opt.removeAttribute("data-combobox-navigate");
    });
    const firstOption = this.el.querySelector(".combobox-option");
    if (firstOption) {
      if (firstOption.getAttribute("data-combobox-disabled") != "true") {
        firstOption.setAttribute("data-combobox-navigate", "");
      }
      firstOption.scrollIntoView({ block: "nearest" });
      this.lastNavigatedValue = firstOption.dataset.comboboxValue;
      if (firstOption.id) this.openButton.setAttribute("aria-activedescendant", firstOption.id);
    }
  },

  // A LiveView patch can move the trigger while the dropdown is open: a form
  // that grows a field, a section that appears, a modal that recentres. The
  // fixed coordinates below are only as good as the layout they were taken
  // from, so recompute them once the DOM has settled.
  updated() {
    if (!this.dropdown || this.isHidden()) return;

    this.updateDropdownPosition();
  },

  // Position the dropdown with `position: fixed`, anchored to the trigger's
  // viewport rect, so it escapes any `overflow: hidden/auto` ancestor (e.g. the
  // device catalog's scroll container) that would otherwise clip it. Recomputed
  // on open, on ancestor scroll / resize, and after a patch, so it tracks the
  // trigger.
  updateDropdownPosition() {
    const rect = this.openButton.getBoundingClientRect();
    const dropdownHeight = this.dropdown.offsetHeight || 200;
    const spaceBelow = window.innerHeight - rect.bottom;
    const above = spaceBelow < dropdownHeight && rect.top > spaceBelow;

    const style = this.dropdown.style;
    style.position = "fixed";
    style.left = `${rect.left}px`;
    style.width = `${rect.width}px`;
    // Sit above the modal layer (z-50) so the dropdown is never occluded.
    style.zIndex = "1000";
    style.bottom = "auto";

    if (above) {
      style.top = `${Math.max(0, rect.top - dropdownHeight)}px`;
      this.dropdown.setAttribute("data-orientation", "above");
      this.openButton.setAttribute("data-orientation", "above");
    } else {
      style.top = `${rect.bottom}px`;
      this.dropdown.setAttribute("data-orientation", "below");
      this.openButton.setAttribute("data-orientation", "below");
    }
  },

  resetDropdownPosition() {
    const style = this.dropdown.style;
    style.position = "";
    style.left = "";
    style.width = "";
    style.top = "";
    style.bottom = "";
    style.zIndex = "";
  },

  handleSearch(e) {
    const query = e.target.value.toLowerCase();
    this.dropdownOptions = this.el.querySelectorAll(".combobox-option");

    this.dropdownOptions.forEach((option) => {
      const valueAttr = option.getAttribute("data-combobox-value") || "";
      const labelText = option.textContent;
      const isFound =
        valueAttr.toLowerCase().includes(query) || labelText.toLowerCase().includes(query);
      option.style.display = isFound ? "" : "none";
    });

    const noResults = this.el.querySelector(".no-results");
    const visibleOptions = Array.from(this.dropdownOptions).filter(
      (option) => option.style.display !== "none",
    );
    if (visibleOptions.length === 0) {
      noResults.classList.remove("hidden");
    } else {
      noResults.classList.add("hidden");
    }

    this.el.querySelectorAll(".option-group").forEach((group) => {
      const visibleInGroup = group.querySelectorAll(
        '.combobox-option:not([style*="display: none"])',
      );
      group.style.display = visibleInGroup.length === 0 ? "none" : "";
    });

    this.dropdownOptions.forEach((option) => option.removeAttribute("data-combobox-navigate"));
    if (visibleOptions.length > 0) {
      if (visibleOptions[0].getAttribute("data-combobox-disabled") != "true") {
        visibleOptions[0].setAttribute("data-combobox-navigate", "");
      }
    }
  },

  handleOptionClick(optionBtnEl, e) {
    if (optionBtnEl.contains(e.target)) {
      e.preventDefault();
      // Look up the <option> fresh rather than caching it at mount: if
      // LiveView replaces the node wholesale on a server re-render (rather
      // than patching it in place), a cached reference would point at a
      // detached node and clicks would silently no-op.
      const value = optionBtnEl.getAttribute("data-combobox-value");
      const option = this.el.querySelector(`option[value="${value}"]`);
      if (option && !option.disabled) {
        // Multi-select pills toggle on/off. Single-select must always select on
        // click (never toggle off) so clicking an option that's already selected
        // (e.g. its "selected" state was never visually cleared) still selects
        // it, instead of silently deselecting.
        const onOrOff = this.select.multiple ? undefined : true;
        this.toggleOption(option, optionBtnEl, onOrOff);
        this.dispatchChangeEvent();

        if (!this.select.multiple) {
          this.closeDropdown();
          this.openButton.focus();
          this.openButton.scrollIntoView({ block: "nearest", inline: "nearest" });
        }
      }
    }
  },

  toggleOption(option, optionBtnEl, on_or_off) {
    if (option) {
      const result = on_or_off === undefined ? !option.selected : on_or_off;
      if (result && !this.select.multiple) this.deselectAll(true);
      option.selected = result;
      optionBtnEl =
        optionBtnEl ||
        this.el.querySelector(`.combobox-option[data-combobox-value="${option.value}"]`);

      if (!optionBtnEl) {
        return;
      }

      if (option.selected) {
        if (!option.disabled) {
          option.setAttribute("selected", "");
          optionBtnEl.setAttribute("data-combobox-selected", "");
          optionBtnEl.setAttribute("aria-selected", "true");
          if (this.select.multiple) {
            this.insertPill(option, optionBtnEl);
          } else {
            this.selectedDisplay.innerHTML = optionBtnEl
              ? optionBtnEl.innerHTML
              : option.textContent;
          }
        }
      } else {
        option.removeAttribute("selected");
        optionBtnEl.removeAttribute("data-combobox-selected");
        optionBtnEl.setAttribute("aria-selected", "false");
        if (this.select.multiple) this.removePill(option.value);
      }

      this.showPlaceholderIfNone();

      return { option, optionBtnEl, result: on_or_off };
    } else {
      return { option, optionBtnEl: null, result: null };
    }
  },

  syncDisplayFromSelect() {
    this.select.querySelectorAll("option[selected]").forEach((option) => {
      this.toggleOption(option, null, true);
    });
    this.showPlaceholderIfNone();
  },

  showPlaceholderIfNone() {
    const anySelected = this.select.querySelector("option[selected]:not([value=''])");

    if (anySelected) {
      if (this.placeholder) this.placeholder.style.display = "none";
      if (this.clearButton) this.clearButton.hidden = false;
    } else {
      this.resetNavigateToFirstOption();
      this.selectedDisplay.innerHTML = "";
      if (this.placeholder) this.placeholder.style.display = "";
      if (this.clearButton) this.clearButton.hidden = true;
    }
  },

  removePill(value) {
    const pill = this.el.querySelector(`[data-combobox-pill-value="${value}"]`);
    if (pill) pill.remove();
  },

  insertPill(option, optionBtnEl) {
    const pill = document.createElement("span");
    pill.classList.add(...this.pillClasses);

    if (optionBtnEl) {
      const optionContent = optionBtnEl.querySelector("[data-combobox-option-content]");
      if (optionContent) {
        const clone = optionContent.cloneNode(true);
        clone.removeAttribute("data-combobox-option-content");
        pill.innerHTML = clone.innerHTML;
      } else {
        pill.textContent = option.textContent;
      }
    } else {
      pill.textContent = option.textContent;
    }
    pill.setAttribute("data-combobox-pill-value", option.value);

    const removeBtn = document.createElement("span");
    removeBtn.innerHTML = this.selectedDisplay.previousElementSibling.innerHTML;
    const handler = this.handleClearOption.bind(this, pill, optionBtnEl, option);
    removeBtn.addEventListener("click", handler);
    pill.appendChild(removeBtn);
    this.selectedDisplay.appendChild(pill);
  },

  handleClearOption(pill, optionBtnEl, option, e) {
    if (pill.contains(e.target)) {
      e.preventDefault();
      e.stopPropagation();
      this.toggleOption(option, optionBtnEl, false);
      this.dispatchChangeEvent();
    }
  },

  handleKeyDown(e) {
    if (e.key === "Escape") {
      e.stopPropagation();
      this.closeDropdown();
      return;
    }

    if (e.shiftKey && e.key === "Tab") {
      e.preventDefault();
      this.closeDropdown();
      this.focusElement(-1);
      return;
    }

    if (e.key === "Tab") {
      this.closeDropdown();
      return;
    }

    if (
      document.activeElement === this.searchInput &&
      !["ArrowDown", "ArrowUp", "Enter"].includes(e.key)
    ) {
      return;
    }

    if (this.isHidden()) return;

    const visibleOptions = Array.from(this.el.querySelectorAll(".combobox-option")).filter(
      (opt) => opt.style.display !== "none",
    );
    if (visibleOptions.length === 0) return;

    let currentIndex = visibleOptions.findIndex((opt) =>
      opt.hasAttribute("data-combobox-navigate"),
    );

    if (e.key === "ArrowDown") {
      currentIndex = (currentIndex + 1) % visibleOptions.length;
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      currentIndex = (currentIndex - 1 + visibleOptions.length) % visibleOptions.length;
      e.preventDefault();
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (currentIndex >= 0) visibleOptions[currentIndex].click();
      if (!this.select.multiple) this.closeDropdown();
      return;
    } else if (e.key.length === 1) {
      e.preventDefault();
      this.handleCharacterNavigation(e.key.toLowerCase(), visibleOptions);
      return;
    } else {
      return;
    }

    visibleOptions.forEach((opt) => opt.removeAttribute("data-combobox-navigate"));
    visibleOptions[currentIndex].setAttribute("data-combobox-navigate", "");
    visibleOptions[currentIndex].scrollIntoView({ block: "nearest" });
    this.lastNavigatedValue = visibleOptions[currentIndex].dataset.comboboxValue;
    if (visibleOptions[currentIndex].id) {
      this.openButton.setAttribute("aria-activedescendant", visibleOptions[currentIndex].id);
    } else {
      this.openButton.removeAttribute("aria-activedescendant");
    }
  },

  handleCharacterNavigation(char, options) {
    const matchingOptions = options.filter((opt) => {
      const valueAttr = opt.getAttribute("data-combobox-value") || "";
      const labelText = opt.textContent.trim().toLowerCase();
      return valueAttr.toLowerCase().startsWith(char) || labelText.startsWith(char);
    });
    if (matchingOptions.length === 0) return;

    let currentIndex = matchingOptions.findIndex((opt) =>
      opt.hasAttribute("data-combobox-navigate"),
    );
    currentIndex = (currentIndex + 1) % matchingOptions.length;

    options.forEach((opt) => opt.removeAttribute("data-combobox-navigate"));
    matchingOptions[currentIndex].setAttribute("data-combobox-navigate", "");
    matchingOptions[currentIndex].scrollIntoView({ block: "nearest" });
  },

  handleDocumentClick(e) {
    if (this.isHidden()) return;
    if (!this.el.contains(e.target) && !this.isClickOnScrollbar(e)) {
      this.closeDropdown();
    }
  },

  isClickOnScrollbar(e) {
    return (
      e.clientX >= document.documentElement.clientWidth ||
      e.clientY >= document.documentElement.clientHeight
    );
  },

  dispatchChangeEvent() {
    const changeEvent = new Event("change", { bubbles: true });
    this.select.dispatchEvent(changeEvent);
  },

  handleClearButtonClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if (this.el.contains(e.target)) {
      this.deselectAll(true);
      this.dispatchChangeEvent();
      this.closeDropdown();
      this.resetNavigateToFirstOption();
    }
  },

  deselectAll(actual = false) {
    if (actual) {
      Array.from(this.select.options).forEach((option) => {
        option.selected = false;
        option.removeAttribute("selected");
      });
    }

    this.el.querySelectorAll(".combobox-option[data-combobox-selected]").forEach((btnEl) => {
      const value = btnEl.getAttribute("data-combobox-value");
      btnEl.removeAttribute("data-combobox-selected");
      btnEl.setAttribute("aria-selected", "false");
      this.removePill(value);
    });

    this.showPlaceholderIfNone();
  },

  destroyed() {
    if (!this.dropdown || !this.openButton) return;

    this.closeDropdown();

    this.observer.disconnect();
    document.removeEventListener("scroll", this.boundHandleScroll, { capture: true });
    window.removeEventListener("resize", this.boundHandleScroll);
    document.removeEventListener("click", this.boundHandleDocumentClick, true);
    document.removeEventListener("keydown", this.boundHandleKeyDown);

    if (this.openButton && this.boundOpenButtonClick) {
      this.openButton.removeEventListener("click", this.boundOpenButtonClick);
    }
    if (this.select && this.boundSelectFocus) {
      this.select.removeEventListener("focus", this.boundSelectFocus);
    }
    if (this.searchInput && this.boundSearchInputHandler) {
      this.searchInput.removeEventListener("input", this.boundSearchInputHandler);
    }
    if (this.clearButton && this.boundClearButtonClick) {
      this.clearButton.removeEventListener("click", this.boundClearButtonClick);
    }
  },

  focusable(includeId) {
    return [this.focusableSelector, `#${includeId}`].join(", ");
  },

  focusElement(advance = 1) {
    if (this.select.form) {
      const focusableEls = Array.from(
        document.querySelectorAll(this.focusable(document.activeElement.id)),
      ).filter(
        (element) =>
          element.offsetWidth > 0 ||
          element.offsetHeight > 0 ||
          element.id === document.activeElement.id,
      );
      const index = focusableEls.indexOf(document.activeElement);
      if (index > -1) {
        let nextElement = focusableEls[index + advance] || focusableEls[0];
        if (document.activeElement.classList.contains("combobox-search-input")) {
          if (advance < 0) advance--;
          if (advance > 0) advance++;
        }

        if (nextElement.classList.contains("combobox-trigger")) {
          let actualEl = nextElement.form.querySelector(nextElement.getAttribute("data-for"));
          if (actualEl) nextElement = actualEl;
        }
        nextElement.focus();
        nextElement.scrollIntoView({ block: "nearest", inline: "nearest" });
      }
    }
  },
};
